<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';
import { useLoading } from 'vue-loading-overlay'
import Layout from "@/layouts/mainfw.vue";
import PageHeader from "@/components/page-header";
import { useFastWay } from "@/state/modules/fastway";
import Dialog from '@/views/components/Dialog.vue';
import Confirm from '@/views/components/Confirm.vue';
import { DatePickerComponent } from "@syncfusion/ej2-vue-calendars";
import { Toast } from 'bootstrap';

const router = useRouter();
const route = useRoute();
const store = useFastWay();
const loader = useLoading();

const showToast = (message, variant = 'success') => {
  const container = document.getElementById('toast-container');
  if (!container) return;

  const bgClass = variant === 'success' ? 'bg-success' : variant === 'danger' ? 'bg-danger' : 'bg-primary';

  const toastHtml = `
    <div class="toast align-items-center ${bgClass} text-white border-0" role="alert" aria-live="assertive" aria-atomic="true">
      <div class="d-flex">
        <div class="toast-body">${message}</div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
    </div>
  `;

  container.insertAdjacentHTML('beforeend', toastHtml);
  const toastElement = container.lastElementChild;
  const bsToast = new Toast(toastElement, { delay: 3000 });
  bsToast.show();

  toastElement.addEventListener('hidden.bs.toast', () => {
    toastElement.remove();
  });
};
let id = ref(0);
let loaded = ref(false);
let editing = ref(false);
let dialog = ref(null);
let dialogDelete = ref(null);
let confirm = ref(null);
let data = ref(null);
let data_local = ref({
  id: 0,
  date: null
});
const title = "Fecha de cierre";
const items = [
  {
    text: "Tablero",
    href: "/",
  },
  {
    text: "Fechas de cierre",
    href: "/sbg/projects/close_dates",
  },
  {
    text: "Fecha de cierre",
    active: true,
  }
];

const api = computed({
    get() {
      return store.parameters.apiURL;
    }
});

// The API returns/expects dates as UTC midnight (e.g. 2026-05-31T00:00:00Z).
// Converting them with `new Date(value)` and displaying in local time can shift
// the day back or forward depending on the browser's timezone offset. These
// helpers keep the calendar day intact regardless of the local timezone.
const toDateOnly = (value) => {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) {
    return null;
  }
  return new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
};

const toIsoDateOnly = (value) => {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) {
    return null;
  }
  return new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())).toISOString();
};

onMounted(async () => {
  id.value = parseInt(route.params.id);
  await getData(id.value);
});

const getData = async (id) => {
  if (id == '0') {
    loaded.value = true;
    editing.value = true;
  } else {
    const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Cargando datos de la fecha de cierre'});
    await axios
    .post(`${api.value}/sbg/projects/close_dates/data`, {id, context: store.userInfo})
    .then(response => {
      if (response.data.success) {
        if (response.data.data) {
          data.value = response.data.data;
          data_local.value = JSON.parse(JSON.stringify(response.data.data));
          data_local.value.date = toDateOnly(data_local.value.date);
        }
      } else {
        dialog.value.show('error', 'Error interno al cargar fecha de cierre', response.data.message);
      }
      loaded.value = true;
      loading.hide();
    })
    .catch(error => {
      loaded.value = true;
      loading.hide();
      dialog.value.show('error', `Error al cargar fecha de cierre ${id}`, error.message);
    });
  }
};

const resetData = () => {
  if (id.value == '0') {
    router.push({name: 'projectsCloseDates'});
  } else {
    data_local.value = JSON.parse(JSON.stringify(data.value));
    data_local.value.date = toDateOnly(data_local.value.date);
    editing.value = false;
  }
};

const validateForm = () => {
  let validated = false;
  if (!data_local.value.date) {
    dialog.value.show('error', 'Error al guardar fecha de cierre', 'Debe especificar la fecha');
  } else {
    validated = true;
  }
  return validated;
};

const save = async () => {
  if (validateForm()) {
    const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Guardando datos de la fecha de cierre'});
    data_local.value.id = id.value;
    data_local.value.context = store.userInfo;
    const payload = JSON.parse(JSON.stringify(data_local.value));
    payload.date = toIsoDateOnly(data_local.value.date);
    await axios
    .post(`${api.value}/sbg/projects/close_dates/save`, payload)
    .then(response => {
      loading.hide();
      if (response.data.success) {
        data.value = response.data.data;
        data_local.value = JSON.parse(JSON.stringify(response.data.data));
        data_local.value.date = toDateOnly(data_local.value.date);
        editing.value = false;
        showToast('Datos han sido actualizados', 'success');
        if (id.value == 0) {
          router.push({
            name: 'projectsCloseDate',
            params: {id: response.data.data.id}
          });
        }
      } else {
        showToast(response.data.message, 'danger');
      }
    })
    .catch(() => {
      loading.hide();
      showToast(`Error al actualizar fecha de cierre ${id.value}`, 'danger');
    });
  }
};

const remove = async () => {
  const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Eliminando fecha de cierre'});
  const parms = {
    id: id.value,
    context: store.userInfo
  };
  await axios
  .post(`${api.value}/sbg/projects/close_dates/delete`, parms)
  .then(response => {
    loading.hide();
    if (response.data.success) {
      dialogDelete.value.show('success', 'Información', 'Fecha de cierre fue eliminada correctamente');
    } else {
      dialog.value.show('error', 'Error al eliminar', response.data.message);
    }
  })
    .catch(() => {
      loading.hide();
      showToast(`Error al eliminar fecha de cierre ${id.value}`, 'danger');
    });
};

const add = () => {
  router.push({
    name: 'projectsCloseDate',
    params: {id: 0}
  });
  id.value = 0;
  data_local.value = {
    id: 0,
    date: null
  };
  editing.value = true;
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
     <div class="row">
        <div class="col-lg-12">
          <div class="card mb-2">
              <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center align-middle row ms-0 me-0 mb-2">
                      <div v-if="!editing" class="col d-flex justify-content-start">
                        <b-button variant="success" class="btn-label waves-effect waves-light" v-b-tooltip.hover title="Agregar fecha de cierre" @click="add"><i class="las la-plus label-icon"></i> Agregar</b-button>
                      </div>
                      <div class="col"/>
                      <div class="input-group col justify-content-end">
                        <b-button v-if="editing" variant="primary" class="btn-label waves-effect waves-light" v-b-tooltip.hover title="Guardar cambios efectuados" @click="save"><i class="las la-save label-icon"></i> Guardar</b-button>
                        <b-button v-if="editing" variant="warning" class="btn-label waves-effect waves-light" v-b-tooltip.hover title="Descartar cambios efectuados" @click="resetData"><i class="las la-undo label-icon"></i> Cancelar</b-button>
                        <b-button v-if="!editing" variant="primary" class="btn-label waves-effect waves-light" v-b-tooltip.hover title="Editar información" @click="editing = true"><i class="las la-edit label-icon"></i> Editar</b-button>
                        <b-button v-if="!editing" variant="danger" class="btn-label waves-effect waves-light" v-b-tooltip.hover title="Eliminar fecha de cierre" @click="confirm.show('Confirme', '¿Está seguro de eliminar esta fecha de cierre?')"><i class="las la-trash label-icon"></i> Eliminar</b-button>
                      </div>
                  </div>
                  <hr>
                  <div class="row mb-2">
                    <div class="col-3">
                      <div class="form-floating">
                        <DatePickerComponent v-model:value="data_local.date" floatLabelType="Auto" placeholder="Fecha" format="dd/MM/yyyy" locale="es-GT" :enabled="editing"/>
                      </div>
                    </div>
                  </div>
              </div>
          </div>
        </div>
    </div>
    <Dialog ref="dialog"/>
    <Dialog ref="dialogDelete" @accept="router.push({name: 'projectsCloseDates'});"/>
    <Confirm ref="confirm" @accept="remove"/>
    <div id="toast-container" class="toast-container position-fixed bottom-0 end-0 p-3" style="z-index: 9999;"></div>
  </Layout>
</template>

<style scoped>
@import "../../../../node_modules/@syncfusion/ej2-base/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-vue-buttons/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-vue-inputs/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-popups/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-lists/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-vue-calendars/styles/material.css";

.e-input-group .e-input[disabled], .e-input-group.e-control-wrapper .e-input[disabled], .e-input-group.e-disabled, .e-input-group.e-control-wrapper.e-disabled, .e-float-input input[disabled], .e-float-input.e-control-wrapper input[disabled], .e-float-input textarea[disabled], .e-float-input.e-control-wrapper textarea[disabled], .e-float-input.e-disabled, .e-float-input.e-control-wrapper.e-disabled {
    -webkit-text-fill-color: rgba(0, 0, 0, 0.75);
    color: rgba(0, 0, 0, 0.75);
}
</style>
